import { capitalize } from '@cdab/scania/qpr/components/atoms'
import { useFeatures } from '@cdab/scania/qpr/feature-flags'
import { TdsDivider, TdsIcon } from '@scania/tegel-react'
import Bold from '@tiptap/extension-bold'
import BulletList from '@tiptap/extension-bullet-list'
import CharacterCount from '@tiptap/extension-character-count'
import Document from '@tiptap/extension-document'
import Heading from '@tiptap/extension-heading'
import History from '@tiptap/extension-history'
import Italic from '@tiptap/extension-italic'
import Link from '@tiptap/extension-link'
import ListItem from '@tiptap/extension-list-item'
import OrderedList from '@tiptap/extension-ordered-list'
import Paragraph from '@tiptap/extension-paragraph'
import Placeholder from '@tiptap/extension-placeholder'
import Strike from '@tiptap/extension-strike'
import Text from '@tiptap/extension-text'
import Underline from '@tiptap/extension-underline'
import { useEditor } from '@tiptap/react'
import parse from 'html-react-parser'
import { useRef } from 'react'
import {
  RichControlsWrapper,
  StyledContentWrapper,
  StyledEditorContent,
  StyledFakeToolbarDiv,
  StyledTextareaContainer,
  StyledTextareaCounter,
  StyledTextareaHelper,
  StyledTextareaWrapper,
  StyledTextContent,
  StyledTextcounterDivider,
  StyledTextfieldData
} from './editor.styles'
import { RichTextOptions } from './rich-text-options'

export const Editor = ({
  autofocus = false,
  content,
  hasError = false,
  label,
  // disabled, // TODO: Implement me
  onBlur,
  maxLength,
  onChange,
  placeholder = '',
  showEditor = true,
  onFocusTextarea,
  modeVariant = 'secondary',
  ...props
}: {
  autofocus?: boolean
  content?: string
  hasError?: boolean
  // disabled?: boolean
  maxLength?: number
  helper?: string | false | null
  label?: string
  onBlur?: (length: number) => void
  onChange?: (value: string) => void
  placeholder?: string
  showEditor?: boolean
  onFocusTextarea?: () => void
  modeVariant?: 'primary' | 'secondary'
}) => {
  const showingEditor = useRef(showEditor)
  const { render_html } = useFeatures(['render_html'])

  //all new possibilities to add line breaks should be added to getFirstLineFromHtmlText util function as well
  const editor = useEditor({
    extensions: [
      Bold,
      BulletList,
      CharacterCount.configure({
        limit: maxLength ?? 0
      }),
      Document,
      Heading.configure({
        levels: [4, 5, 6]
      }),
      History,
      Italic,
      ListItem,
      OrderedList,
      Paragraph,
      Placeholder.configure({
        placeholder
      }),
      Strike,
      Text,
      Underline,
      Link.configure({
        HTMLAttributes: { class: 'tds-link' },
        autolink: true,
        linkOnPaste: true
      })
    ],
    content,
    autofocus,
    editable: true,
    editorProps: {
      attributes: {
        class: 'editor'
      }
    },

    onBlur({ editor, event }) {
      //we shouldn't trigger after clicking the toolbar button
      if (event.relatedTarget == null)
        onBlur?.(editor.storage['characterCount']?.characters())
    },
    onUpdate({ editor }) {
      onChange?.(editor.getHTML())
    }
  })

  if (!editor) return null

  // after enabling the editor we should do focus
  if (!showingEditor.current && showEditor && !editor.isFocused && autofocus) {
    editor.commands.focus()
  }
  showingEditor.current = showEditor

  const helper = props.helper ? capitalize(props.helper) : ''

  return (
    <div className={`sc-tds-textarea-h sc-tds-textarea-s hydrated`}>
      <StyledTextfieldData className='tds-textfield-data'>
        <div className='tds-textfield-slot-wrap-label'>
          {label && <span className='tds-text-label'>{label}</span>}
        </div>
        {showEditor ? (
          <>
            <RichControlsWrapper>
              <RichTextOptions editor={editor} />
            </RichControlsWrapper>
            <TdsDivider variant='discrete' />
          </>
        ) : (
          <StyledFakeToolbarDiv />
        )}
        <StyledTextareaContainer
          className={`textarea-container tds-mode-variant-${modeVariant}sc-tds-textarea ${
            hasError ? 'textarea-error' : ''
          }`}
        >
          <StyledTextareaWrapper className='textarea-wrapper sc-tds-textarea'>
            {showEditor ? (
              <StyledEditorContent
                editor={editor}
                className={`tds-mode-variant-${modeVariant}`}
              />
            ) : (
              <StyledContentWrapper
                onClick={() => onFocusTextarea && onFocusTextarea()}
              >
                <StyledTextContent
                  hasError={hasError}
                  className={`tds-mode-variant-${modeVariant}`}
                >
                  {render_html.enabled ? (
                    content ? (
                      <p>{parse(content)}</p>
                    ) : (
                      content
                    )
                  ) : (
                    content
                  )}
                </StyledTextContent>
              </StyledContentWrapper>
            )}
          </StyledTextareaWrapper>

          <StyledTextareaHelper
            className={`tds-textfield-helper sc-tds-textarea`}
            style={{
              color: !hasError
                ? 'var(--tds-textarea-helper-error)'
                : 'var(--tds-textarea-helper)'
            }}
          >
            {hasError && <TdsIcon name='error' size='16' />}
            <span className='textarea-helper sc-tds-textarea'>{helper}</span>
          </StyledTextareaHelper>
          {maxLength && maxLength > 0 && (
            <StyledTextareaCounter
              modeVariant={modeVariant}
              className='textarea-textcounter sc-tds-textarea'
              style={{
                flexBasis: hasError ? 'auto' : '100%'
              }}
            >
              {editor.storage['characterCount']?.characters()}
              <StyledTextcounterDivider className='textfield-textcounter-divider sc-tds-textarea'>
                {' '}
                /{' '}
              </StyledTextcounterDivider>
              {maxLength}
            </StyledTextareaCounter>
          )}
        </StyledTextareaContainer>
      </StyledTextfieldData>
    </div>
  )
}
